import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MOVE_OUT_ASK_IF_RELOCATION_ROUTE, MOVE_OUT_ROUTE, MOVE_OUT_STATUS_ROUTE } from '@mwe/constants';
import { Address, createAddress, IBankDetails, IERechnungsInfo, ILSystem, MoveOutMapping, Product, Verrechnungskonto } from '@mwe/models';
import {
  getAccountCustomerId,
  getAccountOwner,
  getBusinessDataFromPartnerDetail,
  getCategorySequenceDiff,
  getInvoiceAddress,
  getInvoiceEmail,
  getInvoiceRecipient,
  getPersonalDataFromPartnerDetail,
  hasClearingAccountSepaMandant,
  isArrayWithMinOneItem,
  isCancelledRelocationOrMoveOut,
  isProductCategoryEmobility,
  isProductCategoryFernwaerme,
  isProductCategoryInternet,
  sortProductsEnumCategory,
} from '@mwe/utils';
import { AccountLogic } from '../account/account.logic';
import { MoveOutStateService } from './move-out-state.service';

@Injectable({
  providedIn: 'root',
})
export class MoveOutLogic {
  private readonly moveOutStateService = inject(MoveOutStateService);
  private readonly router = inject(Router);
  private readonly accountLogic = inject(AccountLogic);

  stepOrder = ['erechnung', 'sepa', 'moveout'];

  get products(): Product[] {
    return this.moveOutStateService.products;
  }

  async startProcessWithoutAddressSelection(products: Product[], address: Address): Promise<void> {
    this.moveOutStateService.products = products;
    this.moveOutStateService.address = address;
    this.moveOutStateService.isAddressSelectionVisible = false;
    this.moveOutStateService.addressDetailsLinkType = address?.addressGroup?.detailsLinkType;
    this.router.navigateByUrl(`/${MOVE_OUT_ROUTE}/${MOVE_OUT_ASK_IF_RELOCATION_ROUTE}`);
  }

  startProcessWithAddressSelection(): void {
    this.moveOutStateService.isAddressSelectionVisible = true;

    this.router.navigateByUrl(`/${MOVE_OUT_ROUTE}`);
  }

  canCategoryBeMovedOut(category: string): boolean {
    return !isProductCategoryInternet(category);
  }

  canProcessNotBeDone(): boolean {
    return (
      this.canAllCategoriesNotBeMovedOut() ||
      (!!this.moveOutStateService.addressDetailsLinkType &&
        !isCancelledRelocationOrMoveOut(this.moveOutStateService.addressDetailsLinkType))
    );
  }

  canAllCategoriesNotBeMovedOut(): boolean {
    if (!isArrayWithMinOneItem(this.products)) {
      return true;
    }

    const categories = this.products.map(p => p.category).filter(c => !isProductCategoryEmobility(c));
    return categories.every(cat => !this.canCategoryBeMovedOut(cat));
  }

  getAllProductsWhichCanBeMovedOut(): Product[] {
    return this.products.filter(p => this.canCategoryBeMovedOut(p.category) && !isProductCategoryEmobility(p.category));
  }

  getAllProductsWhichCanNotBeMovedOut(): Product[] {
    const filteredProducts = this.products.filter(p => !this.canCategoryBeMovedOut(p.category));

    if (filteredProducts.some(p => isProductCategoryEmobility(p.category))) {
      return filteredProducts.filter(p => !isProductCategoryEmobility(p.category));
    }

    return filteredProducts;
  }

  createMoveOutMapping(details: Verrechnungskonto[], associatedProducts: Product[]): MoveOutMapping[] {
    return details.map(detail => {
      const accountNumber = detail.verrechnungskontoId;
      let businessPartner = getAccountOwner(detail.geschaeftspartnerBeziehungen);
      let associatedPartnerNumber = businessPartner.geschaeftsPartnerId;
      if (detail.systemId === ILSystem.ISX100) {
        const invoiceRecipient = getInvoiceRecipient(detail.geschaeftspartnerBeziehungen);
        const associatedProduct = associatedProducts.find(ap => ap.systemId === ILSystem.ISX100 && ap.accountNumber === accountNumber);
        // could be the id of a vetragsPartner - if set at all else it is either the id of the account owner or the id of a different invoice recipient
        // in case of a different invoice partner businessPartner = invoiceRecipient == associatedPartner
        associatedPartnerNumber = associatedProduct.businessPartnerNumber;
        businessPartner = invoiceRecipient;
      }
      const businessPartnerNumber = businessPartner.geschaeftsPartnerId;
      const products = this.getAllProductsWhichCanBeMovedOut().filter(p => p.accountNumber === accountNumber);
      const customerNumber = products[0].customerNumber;
      const categories = products.map(p => p.category);
      const tariffClasses = products.map(p => p.tariffClasses);
      const fallBackMail = getInvoiceEmail(detail) || this.accountLogic.getAccountEmail();
      const personalData = getPersonalDataFromPartnerDetail(businessPartner, fallBackMail);
      const businessData = getBusinessDataFromPartnerDetail(businessPartner, fallBackMail);
      const invoiceAddress = getInvoiceAddress(detail.geschaeftspartnerBeziehungen);

      return {
        customerNumber,
        accountNumber,
        businessPartnerNumber,
        associatedPartnerNumber,
        categories,
        tariffClasses,
        personalData,
        businessData,
        invoiceAddress,
      };
    });
  }

  getMoveOutMappingDetails(details: Verrechnungskonto[], moveOutMapping: MoveOutMapping): Verrechnungskonto {
    return details.find(d => {
      let partnershipId = getAccountCustomerId(d.geschaeftspartnerBeziehungen);
      if (isProductCategoryFernwaerme(moveOutMapping.categories[0])) {
        const invoiceRecipient = getInvoiceRecipient(d.geschaeftspartnerBeziehungen);
        if (invoiceRecipient && partnershipId !== invoiceRecipient.geschaeftsPartnerId) {
          partnershipId = invoiceRecipient.geschaeftsPartnerId;
        }
      }
      return partnershipId === moveOutMapping.businessPartnerNumber && d.verrechnungskontoId === moveOutMapping.accountNumber;
    });
  }

  wasSepaChanged(bankDetails: IBankDetails, moveOutDetails: Verrechnungskonto): boolean {
    if (bankDetails?.iban) {
      if (hasClearingAccountSepaMandant(moveOutDetails)) {
        return bankDetails.iban !== moveOutDetails.eingangskonto?.iban;
      }
      return true;
    }
    return false;
  }

  wasPDFEmailChanged(eRechnungsInfo: IERechnungsInfo, moveOutDetails: Verrechnungskonto): boolean {
    if (eRechnungsInfo?.isERechnungsEmpfaenger) {
      const email = getInvoiceEmail(moveOutDetails);
      return eRechnungsInfo.rechnungEmail !== email;
    }
    return false;
  }

  sortMoveOutMappingData(moveOutMappingData: MoveOutMapping[]): MoveOutMapping[] {
    moveOutMappingData.forEach(item => sortProductsEnumCategory(item.categories));
    moveOutMappingData.sort((a, b) => getCategorySequenceDiff(a.categories[0], b.categories[0]));
    moveOutMappingData.forEach((item, ix) => (item.orderIdx = ix));
    return moveOutMappingData;
  }

  initMoveOutStatusProcess(address: Address): void {
    this.router.navigate([`/${MOVE_OUT_ROUTE}/${MOVE_OUT_STATUS_ROUTE}`]).then(() => {
      // to prevent state-clear from wizard/ngOnDestroy
      this.moveOutStateService.address = createAddress(address);
    });
  }

  hasProcessStatusError(processStatus: string): boolean {
    return processStatus.includes('error');
  }

  sortBulletIds(bulletIds: string[]): string[] {
    return bulletIds.sort((a, b) => {
      const stepA = a.split('_');
      const stepB = b.split('_');
      const sortIxDiff = this.stepOrder.indexOf(stepA[0]) - this.stepOrder.indexOf(stepB[0]);
      if (sortIxDiff === 0) {
        return stepA[1] > stepB[1] ? 1 : -1;
      }
      return sortIxDiff > 0 ? 1 : -1;
    });
  }
}
